import type { CallSession } from 'Modules/softphone/types';
import type { Inviter, Invitation } from 'sip.js';

/**
 * Creates an outbound call session object
 * @param inviter - SIP.js Inviter instance
 * @param phoneNumber - Target phone number
 * @param personId - Optional person ID for internal calls
 */
export function createOutboundCallSession(
  inviter: Inviter,
  phoneNumber: string,
  personId?: string
): CallSession {
  // For internal calls (personId exists), use personId as phoneNumber if phoneNumber is empty
  // This ensures we show a meaningful identifier in the UI
  const displayPhoneNumber = phoneNumber || (personId ? `pr${personId}` : '');

  return {
    id: inviter.id,
    direction: 'outbound',
    state: 'connecting',
    phoneNumber: displayPhoneNumber,
    displayName: undefined,
    personId: personId,
    startTime: new Date().toISOString(),
    answerTime: null,
    endTime: null,
    duration: 0,
    isMuted: false,
    isOnHold: false,
    session: inviter,
    conferenceId: null,
  };
}

/**
 * Creates an inbound call session object
 * @param invitation - SIP.js Invitation instance
 * @param phoneNumber - Caller phone number
 * @param displayName - Optional caller display name
 * @param personId - Optional person ID for internal calls
 */
export function createInboundCallSession(
  invitation: Invitation,
  phoneNumber: string,
  displayName?: string,
  personId?: string
): CallSession {
  return {
    id: invitation.id,
    direction: 'inbound',
    state: 'ringing',
    phoneNumber,
    displayName,
    personId,
    startTime: new Date().toISOString(),
    answerTime: null,
    endTime: null,
    duration: 0,
    isMuted: false,
    isOnHold: false,
    session: invitation,
    conferenceId: null,
  };
}
